import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import ImageUpload from '@/components/ImageUpload';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2 } from 'lucide-react';

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title_tr: '',
    title_en: '',
    description_tr: '',
    description_en: '',
    icon: 'zap',
    image_url: '',
    order: 0,
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, formData);
        toast.success('Hizmet güncellendi!');
      } else {
        await axios.post(`${API}/services`, formData);
        toast.success('Hizmet eklendi!');
      }
      fetchServices();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Bir hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu hizmeti silmek istediğinizden emin misiniz?')) return;

    try {
      await axios.delete(`${API}/services/${id}`);
      toast.success('Hizmet silindi!');
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Bir hata oluştu!');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title_tr: service.title_tr,
      title_en: service.title_en,
      description_tr: service.description_tr,
      description_en: service.description_en,
      icon: service.icon,
      image_url: service.image_url || '',
      order: service.order,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      title_tr: '',
      title_en: '',
      description_tr: '',
      description_en: '',
      icon: 'zap',
      image_url: '',
      order: 0,
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 
            className="text-3xl font-bold text-gray-900"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            data-testid="services-title"
          >
            Hizmetler Yönetimi
          </h1>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-service-btn">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Hizmet
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingService ? 'Hizmet Düzenle' : 'Yeni Hizmet Ekle'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="title_tr">Başlık (TR)</Label>
                    <Input
                      id="title_tr"
                      name="title_tr"
                      value={formData.title_tr}
                      onChange={handleChange}
                      required
                      data-testid="service-title-tr"
                    />
                  </div>
                  <div>
                    <Label htmlFor="title_en">Title (EN)</Label>
                    <Input
                      id="title_en"
                      name="title_en"
                      value={formData.title_en}
                      onChange={handleChange}
                      required
                      data-testid="service-title-en"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="description_tr">Açıklama (TR)</Label>
                  <Textarea
                    id="description_tr"
                    name="description_tr"
                    value={formData.description_tr}
                    onChange={handleChange}
                    required
                    rows={3}
                    data-testid="service-desc-tr"
                  />
                </div>
                <div>
                  <Label htmlFor="description_en">Description (EN)</Label>
                  <Textarea
                    id="description_en"
                    name="description_en"
                    value={formData.description_en}
                    onChange={handleChange}
                    required
                    rows={3}
                    data-testid="service-desc-en"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="icon">İkon</Label>
                    <Input
                      id="icon"
                      name="icon"
                      value={formData.icon}
                      onChange={handleChange}
                      placeholder="zap"
                      data-testid="service-icon"
                    />
                  </div>
                  <div>
                    <Label htmlFor="order">Sıra</Label>
                    <Input
                      id="order"
                      name="order"
                      type="number"
                      value={formData.order}
                      onChange={handleChange}
                      data-testid="service-order"
                    />
                  </div>
                </div>
                <ImageUpload
                  label="Hizmet Görseli (opsiyonel)"
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                />
                <Button type="submit" disabled={loading} className="w-full" data-testid="service-submit">
                  {loading ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <div key={service.id} className="bg-white rounded-xl border border-gray-200 p-6" data-testid={`service-card-${service.id}`}>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title_tr}</h3>
              <p className="text-gray-600 mb-4 line-clamp-3">{service.description_tr}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(service)} data-testid={`edit-service-${service.id}`}>
                  <Edit className="w-4 h-4 mr-1" />
                  Düzenle
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(service.id)} data-testid={`delete-service-${service.id}`}>
                  <Trash2 className="w-4 h-4 mr-1" />
                  Sil
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminServices;